import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
} from "ag-grid-community";
import { TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { TemplatePart } from "../../../../models/TemplatePart";

interface Props {
	allParts: TemplatePart[];
	templateParts: TemplatePart[];
	onSelectionChanged: (selectedParts: TemplatePart[]) => void;
	isLoading: boolean;
}

export const PartsTable: React.FC<Props> = ({
	allParts,
	templateParts,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "Name", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allParts.filter(
			(part) => part != null && ((part as any).Name || part.PartName),
		);
	}, [allParts]);

	const columns: ColDef[] = [
		{
			field: "Name",
			headerName: "Part Name",
			minWidth: 120,
			flex: 3,
			sortIndex: 1,
			valueGetter: (params) =>
				(params.data as any).Name || params.data.PartName,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					const aVal = (nodeA.data as any).Name || nodeA.data.PartName;
					const bVal = (nodeB.data as any).Name || nodeB.data.PartName;
					return aVal?.localeCompare(bVal);
				}
			},
		},
		{
			field: "Description",
			headerName: "Description",
			flex: 8,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Description?.localeCompare(nodeB.data.Description);
				}
			},
		},
	];

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			// Select rows based on templateParts
			if (templateParts && templateParts.length > 0) {
				params.api.forEachNode((node) => {
					const nodePart = (node.data as any).Name || node.data.PartName;
					const isSelected = templateParts.some(
						(part) => part.PartName === nodePart,
					);
					if (isSelected) {
						node.setSelected(true);
					}
				});
			}
			sortRows();
		},
		[templateParts],
	);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
			if (allParts.length === 0) gridApi?.showNoRowsOverlay();
		}
	}, [gridApi, isLoading, allParts]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "60vh" }}>
				<Grid2 style={{ width: "100%", height: "60vh" }}>
					<AgGridReact
						getRowId={(params) =>
							(params.data as any).Name || params.data.PartName
						}
						rowHeight={30}
						headerHeight={25}
						animateRows={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={defaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection={{
							mode: "multiRow",
							checkboxes: true,
							headerCheckbox: true,
							enableClickSelection: true,
							hideDisabledCheckboxes: false,
							enableSelectionWithoutKeys: true,
						}}
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
					/>
				</Grid2>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
